/**
 * SlotColumn — one day column of the callback slot picker's grid, shared by the
 * Week view (one recruiter, a column per booking day) and the Team view (one
 * day, a column per recruiter). Quarter-hour cells are buttons; busy time,
 * the call as booked now, the hover ghost, the selection and the now-line are
 * drawn over them with absolute positioning. See CallbackSlotPicker.tsx.
 */
import {
  Busy,
  GridBounds,
  GridCell,
  dayShortLabel,
  minuteInDay,
  placeBlock,
  startOfDay,
  timeLabel,
  timeRangeLabel,
  SLOT_STEP_MIN,
} from './slotLogic';
import type { LaidBusy } from './teamSlotLogic';

/** One quarter-hour row, in px (an hour is 56 px). */
export const ROW_PX = 14;
export const px = (minutes: number) => (minutes / SLOT_STEP_MIN) * ROW_PX;

/** A teammate's private block: hatched grey, so it never reads as one of the viewer's events. */
export const HATCH =
  'bg-gray-100 bg-[repeating-linear-gradient(135deg,transparent_0_4px,rgb(156_163_175/0.35)_4px_8px)] dark:bg-gray-700/60';

export function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-3 w-4 rounded-sm ${swatch}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function busyClass(b: Busy): string {
  if (b.kind === 'callback') return 'border-l-2 border-cyan-500 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/60 dark:text-cyan-200';
  if (b.own) return 'border-l-2 border-slate-400 bg-slate-100 text-slate-700 dark:bg-slate-700/70 dark:text-slate-200';
  return `border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300 ${HATCH}`;
}

/** The lead's name for a callback block ('Callback — Jane Doe' -> 'Jane Doe'), else the label. */
export function busyTitle(b: Busy): string {
  if (b.kind === 'callback') return b.contact_name || b.label.replace(/^Callback\s*[—–-]\s*/, '') || 'Callback';
  return b.label || 'Busy';
}

export function DayColumn({
  day,
  cells,
  height,
  bounds,
  busy,
  laid,
  who,
  dur,
  greyed,
  loading,
  now,
  hover,
  setHover,
  sel,
  current,
  onChoose,
}: {
  day: Date;
  cells: GridCell[];
  height: number;
  bounds: GridBounds;
  busy: Busy[];
  /** Team view: the busy blocks already placed in side-by-side lanes (teamSlotLogic.busyLanes). */
  laid?: LaidBusy[];
  /** Team view: the recruiter's name, so each cell says whose time it is. */
  who?: string;
  dur: number;
  /** Out of office or a closed date: the whole column is greyed (its header says which). */
  greyed: boolean;
  loading: boolean;
  now: number;
  hover: number | null;
  setHover: (ms: number | null) => void;
  sel: { ms: number; conflicted: boolean } | null;
  current: { start: string; durationMin: number } | null;
  onChoose: (cell: GridCell) => void;
}) {
  const blocks: LaidBusy[] = laid
    || busy
      .map((b) => ({ busy: b, at: placeBlock(Date.parse(b.start), Date.parse(b.end), day, bounds) }))
      .filter((x): x is { busy: Busy; at: { top: number; height: number } } => !!x.at)
      .map(({ busy: b, at }) => ({ busy: b, top: at.top, height: at.height, lane: 0, lanes: 1 }));
  const hoverCell = hover !== null ? cells.find((c) => c.startMs === hover) : undefined;
  const selAt = sel ? placeBlock(sel.ms, sel.ms + dur * 60_000, day, bounds) : null;
  const curStart = current ? Date.parse(current.start) : NaN;
  const curAt = current ? placeBlock(curStart, curStart + current.durationMin * 60_000, day, bounds) : null;
  const ghostAt = hoverCell && hoverCell.state !== 'past' && hover !== sel?.ms
    ? placeBlock(hoverCell.startMs, hoverCell.startMs + dur * 60_000, day, bounds)
    : null;
  const nowMin = minuteInDay(now, day);
  const showNow = startOfDay(new Date(now)).getTime() === day.getTime() && nowMin >= bounds.startMin && nowMin < bounds.endMin;
  const dayName = dayShortLabel(day);

  return (
    <div
      className="relative border-l border-gray-200 dark:border-gray-700"
      style={{ height }}
      onMouseLeave={() => setHover(null)}
    >
      {cells.map((c) => {
        const inHours = c.inHours;
        const words = c.state === 'past'
          ? 'in the past'
          : c.state === 'busy'
            ? `taken: ${c.conflicts.map((x) => (x.kind === 'absent' ? 'out of office' : x.label)).join(', ')}`
            : inHours ? 'free' : 'free, outside business hours';
        const line = c.minute % 60 === 0
          ? 'border-t border-gray-200 dark:border-gray-700'
          : c.minute % 30 === 0
            ? 'border-t border-dashed border-gray-100 dark:border-gray-700/50'
            : '';
        const bg = c.state === 'past'
          ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-900/70'
          : inHours
            ? 'cursor-pointer bg-white hover:bg-primary-50/60 dark:bg-gray-800 dark:hover:bg-primary-900/20'
            : 'cursor-pointer bg-gray-100 hover:bg-gray-200/70 dark:bg-gray-900/60 dark:hover:bg-gray-900';
        return (
          <button
            key={c.minute}
            type="button"
            disabled={c.state === 'past' || loading}
            onClick={() => onChoose(c)}
            onMouseEnter={() => setHover(c.startMs)}
            onFocus={() => setHover(c.startMs)}
            aria-label={`${who ? `${who}, ` : ''}${dayName}, ${timeLabel(c.startMs)}, ${words}`}
            aria-pressed={sel?.ms === c.startMs}
            className={`block h-3.5 w-full ${line} ${bg} focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 disabled:cursor-not-allowed`}
          />
        );
      })}

      {greyed && (
        <div
          className="pointer-events-none absolute inset-0 border-x border-dashed border-gray-300 bg-gray-200/50 dark:border-gray-600 dark:bg-gray-700/40"
          aria-hidden="true"
        />
      )}

      {blocks.map(({ busy: b, top, height: h, lane, lanes }, i) => (
        <div
          key={`${b.callback_id || b.start}-${i}`}
          className={`pointer-events-none absolute overflow-hidden rounded-md px-1 py-0.5 text-[10px] leading-tight ${
            lanes > 1 ? '' : 'left-0.5 right-0.5'
          } ${busyClass(b)}`}
          style={{
            top: px(top),
            height: Math.max(px(h), 10),
            ...(lanes > 1 ? { left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)` } : {}),
          }}
          aria-hidden="true"
        >
          <span className="block truncate font-medium">{busyTitle(b)}</span>
          {h >= 30 && (
            <span className="block truncate opacity-80">{timeRangeLabel(Date.parse(b.start), Date.parse(b.end))}</span>
          )}
        </div>
      ))}

      {curAt && (
        <div
          className="pointer-events-none absolute left-0.5 right-0.5 overflow-hidden rounded-md border-2 border-dashed border-primary-500 bg-primary-50/40 px-1 py-0.5 text-[10px] font-medium leading-tight text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
          style={{ top: px(curAt.top), height: Math.max(px(curAt.height), 10) }}
          aria-hidden="true"
        >
          <span className="block truncate">This call now</span>
        </div>
      )}

      {ghostAt && hoverCell && (
        <div
          className={`pointer-events-none absolute left-0.5 right-0.5 overflow-hidden rounded-md border-2 border-dashed px-1 py-0.5 text-[10px] font-medium leading-tight ${
            hoverCell.state === 'busy'
              ? 'border-amber-500 bg-amber-100/80 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
              : 'border-primary-400 bg-primary-100/70 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
          }`}
          style={{ top: px(ghostAt.top), height: Math.max(px(ghostAt.height), 10) }}
          aria-hidden="true"
        >
          <span className="block truncate">{timeRangeLabel(hoverCell.startMs, hoverCell.startMs + dur * 60_000)}</span>
        </div>
      )}

      {selAt && sel && (
        <div
          className={`pointer-events-none absolute left-0.5 right-0.5 overflow-hidden rounded-md bg-primary-600 px-1 py-0.5 text-[10px] font-semibold leading-tight text-white shadow-md ring-2 ${
            sel.conflicted ? 'ring-amber-400' : 'ring-white dark:ring-gray-800'
          }`}
          style={{ top: px(selAt.top), height: Math.max(px(selAt.height), 10) }}
          aria-hidden="true"
        >
          <span className="block truncate">{timeRangeLabel(sel.ms, sel.ms + dur * 60_000)}</span>
        </div>
      )}

      {showNow && (
        <div
          className="pointer-events-none absolute inset-x-0 h-px bg-red-500"
          style={{ top: px(nowMin - bounds.startMin) }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
